import { humanizeKey } from "./pipeline";

/**
 * Renders import params and import errors, which are free-form JSON, as nested
 * lists instead of raw JSON.
 */

// Row errors can list every row of a file
const MAX_ENTRIES = 50;

const isRowKey = (key: string) => /^\d+$/.test(key);

const More = ({ count }: { count: number }) =>
    count > 0 ? (
        <p className="mt-1 text-xs text-ae-slate-600">and {count} more…</p>
    ) : null;

const Scalar = ({ value }: { value: unknown }) => {
    if (value === null || value === undefined || value === "") {
        return <span className="text-ae-slate-600">—</span>;
    }
    if (typeof value === "boolean") {
        return <span>{value ? "Yes" : "No"}</span>;
    }
    const text = String(value);
    // Multi-line strings are tracebacks or database errors
    if (text.includes("\n")) {
        return (
            <pre className="whitespace-pre-wrap break-all rounded bg-ae-slate-100 p-2 font-mono text-xs">
                {text}
            </pre>
        );
    }
    return <span className="whitespace-pre-wrap break-words">{text}</span>;
};

const isScalar = (value: unknown) =>
    value === null || typeof value !== "object";

export const ReadableValue = ({ value }: { value: unknown }) => {
    if (isScalar(value)) {
        return <Scalar value={value} />;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <span className="text-ae-slate-600">None</span>;
        }
        // Form errors come as a list with a single message
        if (value.length === 1) {
            return <ReadableValue value={value[0]} />;
        }
        const shown = value.slice(0, MAX_ENTRIES);
        return (
            <>
                <ul className="list-disc space-y-0.5 pl-4">
                    {shown.map((item, index) => (
                        <li key={index}>
                            <ReadableValue value={item} />
                        </li>
                    ))}
                </ul>
                <More count={value.length - shown.length} />
            </>
        );
    }

    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
        return <span className="text-ae-slate-600">None</span>;
    }
    const shown = entries.slice(0, MAX_ENTRIES);
    return (
        <>
            <dl className="space-y-1">
                {shown.map(([key, item]) => (
                    <div
                        key={key}
                        className={
                            isScalar(item) || Array.isArray(item)
                                ? "grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2"
                                : ""
                        }
                    >
                        <dt className="font-medium text-ae-slate-600 break-words">
                            {/* Importers key row errors by the row number */}
                            {isRowKey(key) ? `Row ${key}` : humanizeKey(key)}
                        </dt>
                        <dd
                            className={
                                isScalar(item) || Array.isArray(item)
                                    ? "min-w-0"
                                    : "mt-1 border-l-2 border-ae-gray-250 pl-3"
                            }
                        >
                            <ReadableValue value={item} />
                        </dd>
                    </div>
                ))}
            </dl>
            <More count={entries.length - shown.length} />
        </>
    );
};
